import axios from 'axios'
import moment from 'moment'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, Button, Modal } from '../components/ui'
import { useToast } from '../components/ui/Toast/Toast'
import { constants } from '../Constants'
import { compressImage } from '../utils/compressImage'
import './MyAccount.scss'

const AVATAR_COLORS = [
  '#2440ab',
  '#3b82f6',
  '#2563eb',
  '#0ea5e9',
  '#06b6d4',
  '#14b8a6',
  '#10b981',
  '#22c55e',
  '#84cc16',
  '#eab308',
  '#f59e0b',
  '#f97316',
  '#ef4444',
  '#ec4899',
  '#d946ef',
  '#a855f7',
  '#8b5cf6',
  '#6366f1',
  '#64748b',
  '#0f766e',
  '#166534',
  '#7c2d12',
  '#991b1b',
  '#7e22ce',
  '#1d4ed8',
]

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return (parts[0]?.[0] || '?').toUpperCase()
}

export const MyAccount = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const fileInputRef = useRef(null)

  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem('user'))
  )
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState({})
  const [loadingSave, setLoadingSave] = useState(false)

  // Avatar picker state
  const [showAvatarPicker, setShowAvatarPicker] = useState(false)
  const [avatarStep, setAvatarStep] = useState('choose') // 'choose' | 'initials'
  const [selectedColor, setSelectedColor] = useState(
    user?.avatarColor || AVATAR_COLORS[0]
  )
  const [loadingAvatar, setLoadingAvatar] = useState(false)

  // Password modal state
  const [pwModalOpen, setPwModalOpen] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loadingPw, setLoadingPw] = useState(false)

  const fullName = `${user?.name || ''} ${user?.lastname || ''}`.trim()
  const initials = getInitials(fullName)

  /* =========== helpers =========== */
  const persistUser = updates => {
    const merged = { ...user, ...updates }
    setUser(merged)
    const stored = JSON.parse(localStorage.getItem('user'))
    localStorage.setItem('user', JSON.stringify({ ...stored, ...updates }))
  }

  const evaluateStrength = pw =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$.!%*#?&]{8,}$/.test(pw)
      ? 'strong'
      : 'weak'

  /* =========== avatar handlers =========== */
  const handlePhotoUpload = async e => {
    const file = e.target.files?.[0]
    if (!file) return
    const validExts = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'heic',
      'heif',
      'bmp',
    ]
    const ext = file.name?.split('.').pop()?.toLowerCase()
    // file.type can be empty on Android content:// URIs — trust accept="image/*" in that case
    if (file.type && !file.type.startsWith('image/') && !validExts.includes(ext)) {
      toast({ title: 'Solo se permiten imágenes', status: 'error' })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'La imagen no puede superar los 5 MB', status: 'error' })
      return
    }

    setLoadingAvatar(true)
    try {
      const base64 = await compressImage(file)

      const { data } = await axios.post(`${constants.urlLocal}upload-avatar`, {
        userId: user.id || user._id,
        image: base64,
      })

      if (data.ok) {
        persistUser({ avatar: data.avatar, avatarType: 'image' })
        setShowAvatarPicker(false)
        setAvatarStep('choose')
        toast({ title: 'Foto actualizada', status: 'success' })
      }
    } catch (err) {
      toast({
        title: 'Error al subir la foto',
        description: err.response?.data?.message || err.message,
        status: 'error',
      })
    } finally {
      setLoadingAvatar(false)
    }
  }

  const handleSaveInitials = async () => {
    setLoadingAvatar(true)
    try {
      const { data } = await axios.post(
        `${constants.urlLocal}save-avatar-initials`,
        {
          userId: user.id || user._id,
          avatarColor: selectedColor,
        }
      )
      if (data.ok) {
        persistUser({
          avatarType: 'initials',
          avatarColor: selectedColor,
          avatar: '',
        })
        setShowAvatarPicker(false)
        setAvatarStep('choose')
        toast({ title: 'Avatar actualizado', status: 'success' })
      }
    } catch (err) {
      toast({
        title: 'Error al guardar avatar',
        description: err.response?.data?.message || err.message,
        status: 'error',
      })
    } finally {
      setLoadingAvatar(false)
    }
  }

  /* =========== profile edit handlers =========== */
  const startEdit = () => {
    setEditData({
      name: user.name,
      lastname: user.lastname,
      email: user.email,
      phone: user.phone,
      dateOfBirth: user.dateOfBirth,
    })
    setEditMode(true)
  }

  const handleInputChange = e => {
    const { name, value } = e.target
    setEditData(prev => ({ ...prev, [name]: value }))
  }

  const handleSaveProfile = async () => {
    setLoadingSave(true)
    try {
      const { data } = await axios.put(`${constants.urlLocal}update`, {
        userId: user.id || user._id,
        ...editData,
      })
      if (data.ok) {
        persistUser({
          name: data.user.name,
          lastname: data.user.lastname,
          email: data.user.email,
          phone: data.user.phone,
          dateOfBirth: data.user.dateOfBirth,
        })
        setEditMode(false)
        toast({ title: 'Datos actualizados', status: 'success' })
      }
    } catch (err) {
      toast({
        title: 'Error al actualizar',
        description: err.message,
        status: 'error',
      })
    } finally {
      setLoadingSave(false)
    }
  }

  /* =========== password handler =========== */
  const handleChangePassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      toast({
        title: 'Las contraseñas no coinciden o están vacías.',
        status: 'error',
      })
      return
    }
    if (evaluateStrength(newPassword) === 'weak') {
      toast({
        title: 'Contraseña débil',
        description:
          'Mínimo 8 caracteres, una mayúscula, una minúscula y un número.',
        status: 'error',
      })
      return
    }
    setLoadingPw(true)
    try {
      const { data } = await axios.put(`${constants.urlLocal}change-password`, {
        userId: user.id || user._id,
        newPassword,
      })
      if (data.message) {
        toast({ title: 'Contraseña actualizada', status: 'success' })
        setPwModalOpen(false)
        setNewPassword('')
        setConfirmPassword('')
      }
    } catch {
      toast({ title: 'Error al cambiar la contraseña', status: 'error' })
    } finally {
      setLoadingPw(false)
    }
  }

  /* =========== logout =========== */
  const handleLogout = () => {
    localStorage.clear()
    navigate('/login')
  }

  /* =========== current avatar src/color logic =========== */
  const avatarSrc = user?.avatarType === 'image' ? user?.avatar : ''
  const avatarBg =
    user?.avatarType === 'initials' ? user?.avatarColor : undefined

  /* =========== RENDER =========== */
  return (
    <div className="profile">
      {/* ── HERO ── */}
      <section className="profile-hero">
        <div className="profile-hero__avatar-wrapper">
          <Avatar
            name={fullName}
            src={avatarSrc}
            bgColor={avatarBg}
            size="xxl"
          />
          <button
            className="profile-hero__edit-btn"
            onClick={() => {
              setShowAvatarPicker(true)
              setAvatarStep('choose')
            }}
            title="Cambiar avatar"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoUpload}
          />
        </div>

        <h1 className="profile-hero__name">{fullName}</h1>
        <span className="profile-hero__role">
          {user?.role === 'admin' ? '⭐ Administrador' : '🏋️ Atleta'}
        </span>
      </section>

      {/* ── AVATAR PICKER (inline card) ── */}
      {showAvatarPicker && (
        <div className="profile-card avatar-picker">
          {loadingAvatar ? (
            <div className="profile-spinner">
              <div className="profile-spinner__circle" />
              <p style={{ color: '#555', fontSize: '0.9rem' }}>Procesando...</p>
            </div>
          ) : avatarStep === 'choose' ? (
            <>
              <div className="profile-card__header">
                <h3 className="profile-card__title">📷 Cambiar avatar</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAvatarPicker(false)}
                >
                  ✕
                </Button>
              </div>
              <div className="avatar-picker__options">
                <button
                  className="avatar-picker__option"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className="avatar-picker__option-icon">📸</span>
                  <span className="avatar-picker__option-label">
                    Subir foto
                  </span>
                </button>
                <button
                  className="avatar-picker__option"
                  onClick={() => setAvatarStep('initials')}
                >
                  <span className="avatar-picker__option-icon">🎨</span>
                  <span className="avatar-picker__option-label">
                    Iniciales + color
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="profile-card__header">
                <h3 className="profile-card__title">🎨 Elige tu color</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAvatarStep('choose')}
                >
                  ← Volver
                </Button>
              </div>

              <div className="avatar-picker__preview">
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: selectedColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  {initials}
                </div>
              </div>

              <div className="avatar-picker__colors">
                {AVATAR_COLORS.map(color => (
                  <button
                    key={color}
                    className={`avatar-picker__color-btn${selectedColor === color ? ' avatar-picker__color-btn--selected' : ''}`}
                    style={{ background: color }}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="primary"
                  onClick={handleSaveInitials}
                  disabled={loadingAvatar}
                >
                  Guardar avatar
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── DATOS PERSONALES ── */}
      <div className="profile-card">
        <div className="profile-card__header">
          <h3 className="profile-card__title">👤 Datos personales</h3>
          {!editMode ? (
            <Button variant="primary" size="sm" onClick={startEdit}>
              Editar
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditMode(false)}
            >
              Cancelar
            </Button>
          )}
        </div>

        {editMode ? (
          <div>
            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input
                className="form-input"
                name="name"
                value={editData.name || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Apellido</label>
              <input
                className="form-input"
                name="lastname"
                value={editData.lastname || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                name="email"
                value={editData.email || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input
                className="form-input"
                name="phone"
                value={editData.phone || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha de nacimiento</label>
              <input
                className="form-input"
                type="date"
                name="dateOfBirth"
                value={moment(editData.dateOfBirth).format('YYYY-MM-DD')}
                onChange={handleInputChange}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '0.5rem',
              }}
            >
              <Button
                variant="primary"
                onClick={handleSaveProfile}
                loading={loadingSave}
              >
                Guardar cambios
              </Button>
            </div>
          </div>
        ) : (
          <div className="profile-info">
            <div className="profile-info__row">
              <span className="profile-info__label">Email</span>
              <span className="profile-info__value">{user?.email}</span>
            </div>
            <div className="profile-info__row">
              <span className="profile-info__label">Teléfono</span>
              <span className="profile-info__value">{user?.phone || '—'}</span>
            </div>
            <div className="profile-info__row">
              <span className="profile-info__label">Fecha de nacimiento</span>
              <span className="profile-info__value">
                {user?.dateOfBirth
                  ? moment(user.dateOfBirth).format('DD/MM/YYYY')
                  : '—'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── SEGURIDAD ── */}
      <div className="profile-card">
        <div className="profile-card__header">
          <h3 className="profile-card__title">🔒 Seguridad</h3>
        </div>
        <p className="profile-security__hint">
          Mantén tu contraseña segura. Usa al menos 8 caracteres con mayúsculas,
          minúsculas y números.
        </p>
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setPwModalOpen(true)}
        >
          Cambiar contraseña
        </Button>
      </div>

      {/* ── CERRAR SESIÓN ── */}
      <div className="profile-logout">
        <button className="profile-logout__btn" onClick={handleLogout}>
          Cerrar sesión
        </button>
      </div>

      {/* ── PASSWORD MODAL ── */}
      <Modal
        isOpen={pwModalOpen}
        onClose={() => {
          setPwModalOpen(false)
          setNewPassword('')
          setConfirmPassword('')
          setShowPassword(false)
        }}
        title="Cambiar contraseña"
      >
        <div className="form-group">
          <label className="form-label">Nueva contraseña</label>
          <input
            className="form-input"
            type={showPassword ? 'text' : 'password'}
            placeholder="Introduce la nueva contraseña"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
          {newPassword && (
            <div className="strength-bar" style={{ marginTop: '0.5rem' }}>
              <div
                className={`strength-bar__fill strength-bar__fill--${evaluateStrength(newPassword)}`}
              />
            </div>
          )}
          {newPassword && (
            <p
              style={{
                fontSize: '0.78rem',
                color:
                  evaluateStrength(newPassword) === 'strong'
                    ? '#10b981'
                    : '#ef4444',
                marginTop: '0.25rem',
                fontWeight: 600,
              }}
            >
              {evaluateStrength(newPassword) === 'strong'
                ? '✓ Contraseña fuerte'
                : '✗ Contraseña débil'}
            </p>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Confirmar contraseña</label>
          <input
            className="form-input"
            type={showPassword ? 'text' : 'password'}
            placeholder="Repite la contraseña"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <p
              style={{
                fontSize: '0.78rem',
                color: '#ef4444',
                marginTop: '0.25rem',
                fontWeight: 600,
              }}
            >
              ✗ Las contraseñas no coinciden
            </p>
          )}
        </div>
        <label className="form-checkbox" style={{ marginBottom: '1rem' }}>
          <input
            type="checkbox"
            checked={showPassword}
            onChange={() => setShowPassword(!showPassword)}
          />
          Mostrar contraseña
        </label>
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}
        >
          <Button
            variant="ghost"
            onClick={() => {
              setPwModalOpen(false)
              setNewPassword('')
              setConfirmPassword('')
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            loading={loadingPw}
          >
            Cambiar
          </Button>
        </div>
      </Modal>
    </div>
  )
}
